import { isNumeric } from 'shared/src/utils/isNumeric';
import { isEqual } from 'shared/src/utils/isEqual';
import utils from '../utils';
// eslint-disable-next-line import/no-cycle
import LetterPanel from '../uTheory.Notation/letterPanel';
import type {
  LetterPanelButtonObject,
  LetterPanelButtonOrBRObject,
} from '../uTheory.Notation/letterPanel';
import * as Font from '../uTheory.Font/font';
import Chord from '../uTheory.Notation/chord';
import RomanNumeralButtons from './romanNumeralButtons';
import Note from '../uTheory.Notation/note';
/**
 * convert a string of figured bass to an html formatted string of figured bass
 * @param {string} figures figured bass string
 * @returns {string} html formatted figured bass
 */
const FIGURE = (figures: string): string => {
  const fbSize = figures.length > 2 ? '0.4' : '0.5';
  const fb = `<span style='
    letter-spacing: -${fbSize}em;
    display: inline-block;
    transform: translateX(-0.3em) translateY(0.5em);
    -webkit-writing-mode: vertical-rl;
    writing-mode: vertical-rl;
    -webkit-text-orientation: upright;
    text-orientation: upright;
    font-size: ${fbSize}em;
    vertical-align: top'
  >`;
  const bf = '</span>';
  return fb + figures + bf;
};

type SopranoFactorString = 'root' | 'third' | 'fifth' | 'seventh';

type ChordIdButtonType = 'quality' | 'inversionNumbers' | 'figures' | 'sopranoFactor' | 'uiAction';

const blankAnswer: ChordIdAnswer = {
  roman: {
    numeral: '',
    dimCircle: '',
  },
  root: undefined,
  quality: '',
  inversion: '',
  sopranoFactor: '',
  isSeventh: false,
};

export interface ChordIdButtonsOptions {
  chordTypes: 'triads' | 'sevenths' | 'both';
  /** Whether roman numerals are shown */
  romans?: boolean;
  /** Whether quality labels are shown */
  quality?: boolean;
  /** Whether inversions are shown, and of what type? */
  inversionType?: false | 'figures' | 'numbers';
  /** Whether the root must be clicked */
  root?: boolean;
  /** whether the soprano factor is displayed (root, third, fifth, etc...) */
  sopranoFactor?: boolean;
  /** How many underscores for a blank question? Default: 2 */
  numUnderscoresForBlankQuestion?: number;
  checkButtonText?: string;
}

export interface ChordIdAnswer {
  roman: {
    numeral: string;
    dimCircle: 'o' | 'o/' | '';
  };
  root: Note | undefined;
  quality: string;
  /**
   * When options.inversionType === 'figures', a string representing the figures
   * as entered by the user.
   *
   * When options.inversionType === 'numbers', '0' | '1' | '2' | '3'
   */
  inversion: string;
  sopranoFactor: string;
  isSeventh: boolean;
}

/**
 * ChordIdButtons
 * buttons for use identifying chords, used by letterPanel.
 */
export default class ChordIdButtons {
  letterPanel: LetterPanel;

  options: ChordIdButtonsOptions;

  answer: ChordIdAnswer;

  answerStack: ChordIdAnswer[];

  romans?: RomanNumeralButtons;

  buttons!: LetterPanelButtonOrBRObject[];

  private _answerText: string = '_';

  /**
   * @param {LetterPanel} letterPanel
   * @param {Object} options
   * @param {string} options.chordTypes 'triads', 'sevenths', 'both'
   * @param {boolean} options.romans whether romans are shown
   * @param {boolean} options.quality whether quality lables are shown
   * @param {string|boolean} options.inversionType: false, 'figures', 'numbers'
   * @param {boolean} options.root whether root is displayed
   * @param {sopranoFactor} options.sopranoFactor whether soprano factor is displayed
   * @param {number} options.numUnderscoresForBlankQuestion = 2
   * @constructor
   */
  constructor(letterPanel: LetterPanel, options: Partial<ChordIdButtonsOptions>) {
    if (!(letterPanel instanceof LetterPanel)) {
      throw new Error('letterPanel is required to initialize ChordIdButtons');
    }
    this.letterPanel = letterPanel;

    this.options = utils.merge(
      {
        romans: false,
        chordTypes: 'triads',
        inversionType: false,
        sopranoFactor: false,
        quality: true,
        numUnderscoresForBlankQuestion: 2,
        root: false,
      },
      options || {}
    );

    this.setButtons(this.options);
    this.answer = utils.clone(blankAnswer, true);
    this.answerStack = [utils.clone(this.answer, true)];
  }

  /**
   * @param {Object} options { chordTypes: 'triads', 'sevenths', 'both'
   *                           inversionType: false, 'figures', 'numbers' }
   */
  setButtons(options = this.options): LetterPanelButtonOrBRObject[] {
    const triads = options.chordTypes === 'triads' || options.chordTypes === 'both';
    const sevenths = options.chordTypes === 'sevenths' || options.chordTypes === 'both';

    /**
     * '7' in HMTL figured bass style
     */
    const fontSeven = Font.toHTML('figuredBass', '7');

    /**
     * Pass 7 in the value for the Maj, min and dim quality buttons if:
     *  - we're in sevenths only mode
     *  - the inversion type is NOT figures
     */
    const sev = sevenths && !triads && options.inversionType !== 'figures' ? '7' : '';

    /**
     * Figured bass font 7 or ''.
     *
     * Show 7 in the text for the Maj, min and dim quality buttons if:
     *  - we're in sevenths only mode
     *  - the inversion type is NOT figures
     */
    const mmd7 = sev ? fontSeven : '';

    /**
     * '7' or ''.
     *
     * Should we include a 7 on the Dom quality button?
     *  - If the inversion type is NOT figures, we should
     */
    const domSev = options.inversionType !== 'figures' ? '7' : '';

    // type ButtonDefinition = [string, any, string | string[], string | false, boolean];

    interface ButtonDefinition {
      text: string;
      value: string;
      hotKey: string | string[];
      className: string | false;
      visible: boolean;
      type: ChordIdButtonType;
    }

    const sopranoFactors: ButtonDefinition[] = [
      {
        text: 'Root',
        value: 'root',
        hotKey: ['r', 'R'],
        className: false,
        visible: true,
        type: 'sopranoFactor',
      },
      {
        text: '3rd',
        value: 'third',
        hotKey: ['t', 'T'],
        className: false,
        visible: true,
        type: 'sopranoFactor',
      },
      {
        text: '5th',
        value: 'fifth',
        hotKey: ['f', 'F'],
        className: false,
        visible: true,
        type: 'sopranoFactor',
      },
      {
        text: '7th',
        value: 'seventh',
        hotKey: ['s', 'S'],
        className: false,
        visible: sevenths,
        type: 'sopranoFactor',
      },
    ];

    if (options.romans) {
      const romanOptions = utils.merge(utils.clone(this.options), { diminishedButtons: true });
      this.romans = new RomanNumeralButtons(
        this.letterPanel.lettersDiv,
        this.letterPanel,
        romanOptions
      );
      this.romans.addCallback(this.rnCallback.bind(this));
    }

    // [text, value, hotkey, class, visible]
    const qualities: ButtonDefinition[] = [
      {
        text: `Maj${mmd7}`,
        value: `Maj${sev}`,
        hotKey: 'M',
        className: false,
        visible: triads || sevenths,
        type: 'quality',
      },
      {
        text: `min${mmd7}`,
        value: `min${sev}`,
        hotKey: 'm',
        className: false,
        visible: triads || sevenths,
        type: 'quality',
      },
      {
        text: 'Aug',
        value: 'Aug',
        hotKey: 'A',
        className: false,
        visible: triads,
        type: 'quality',
      },
      // triads only show as 'dim' on button
      {
        text: 'dim',
        value: 'dim',
        hotKey: ['d', 'o'],
        className: false,
        visible: triads && !sevenths,
        type: 'quality',
      },
      // sevenths OR triads && sevenths, show as 'o' instead
      {
        text: `dim${mmd7}`,
        value: `dim${sev}`,
        hotKey: ['d', 'o'],
        className: false,
        visible: sevenths,
        type: 'quality',
      },
      {
        text: `Dom${domSev ? fontSeven : ''}`,
        value: `Dom${domSev ? '7' : ''}`,
        hotKey: 'D',
        className: false,
        visible: sevenths,
        type: 'quality',
      },
      {
        text: Font.toHTML('quality', 'o/') + (domSev ? fontSeven : ''),
        value: `half-dim${domSev ? '7' : ''}`,
        hotKey: ['h', 'alt+o'],
        className: false,
        visible: sevenths,
        type: 'quality',
      },
      {
        text: fontSeven,
        value: '7',
        hotKey: '7',
        className: false,
        visible: sevenths && triads && options.inversionType !== 'figures',
        type: 'quality',
      },
    ];

    const figures = options.inversionType === 'figures';
    const numbers = options.inversionType === 'numbers';

    const qualInvConflict = this.options.sopranoFactor && this.options.inversionType;
    const inversions: ButtonDefinition[] = [
      {
        text: 'Root',
        value: '0',
        hotKey: qualInvConflict ? ['0'] : ['0', 'r', 'R'],
        className: false,
        visible: numbers,
        type: 'inversionNumbers',
      },
      {
        text: '1st',
        value: '1',
        hotKey: '1',
        className: false,
        visible: numbers,
        type: 'inversionNumbers',
      },
      {
        text: '2nd',
        value: '2',
        hotKey: '2',
        className: false,
        visible: numbers,
        type: 'inversionNumbers',
      },
      {
        text: 'Third',
        value: '3',
        hotKey: '3',
        className: false,
        visible: numbers && sevenths,
        type: 'inversionNumbers',
      },
      {
        text: '2',
        value: '2',
        hotKey: '2',
        className: false,
        visible: figures && sevenths,
        type: 'figures',
      },
      { text: '3', value: '3', hotKey: '3', className: false, visible: figures, type: 'figures' },
      { text: '4', value: '4', hotKey: '4', className: false, visible: figures, type: 'figures' },
      { text: '5', value: '5', hotKey: '5', className: false, visible: figures, type: 'figures' },
      { text: '6', value: '6', hotKey: '6', className: false, visible: figures, type: 'figures' },
      {
        text: '7',
        value: '7',
        hotKey: '7',
        className: false,
        visible: figures && sevenths,
        type: 'figures',
      },
    ];

    const others: ButtonDefinition[] = [
      {
        text: 'Undo',
        value: 'undo',
        hotKey: ['backspace', 'del', 'ctrl+z'],
        className: false,
        visible: !!(options.chordTypes || options.inversionType),
        type: 'uiAction',
      },
      {
        text: options.checkButtonText || 'Check',
        value: 'check',
        hotKey: 'enter',
        className: false,
        visible: true,
        type: 'uiAction',
      },
    ];

    const buttonsDefinitions: ButtonDefinition[][] = [];
    if (options.sopranoFactor) buttonsDefinitions.push(sopranoFactors);
    if (options.quality) buttonsDefinitions.push(qualities);
    if (options.inversionType) buttonsDefinitions.push(inversions);
    buttonsDefinitions.push(others);

    this.buttons = buttonsDefinitions
      .map((buttonGroup): LetterPanelButtonObject[] =>
        buttonGroup
          .filter((button) => button.visible)
          .map((button) => {
            const boundCallback = (): void => this.buttonCallback(button.type, button.value);
            const { text, value, hotKey, className } = button;

            const buttonObj: LetterPanelButtonObject = {
              text,
              value,
              hotKey,
              callback: boundCallback,
            };
            if (className) {
              buttonObj.class = className;
            }

            return buttonObj;
          })
      )
      // Remove any empty button groups:
      .filter((buttonGroup) => buttonGroup.length)
      // Insert a {br} after each group:
      .map((buttonGroup): LetterPanelButtonOrBRObject[] => [...buttonGroup, { tagName: 'br' }])
      // And flatten it to a one-dimensional array:
      .flat();

    this.letterPanel.createCustomButtons(this.buttons);
    return this.buttons;
  }

  getButtons(): LetterPanelButtonOrBRObject[] {
    return this.buttons;
  }

  reset(): void {
    this.answer = utils.clone(blankAnswer, true);
    this.answerStack = [utils.clone(this.answer, true)];
    this.updateAnswerDisplay();
  }

  rnCallback(value: string): void {
    this.answerStack.push(utils.clone(this.answer, true));
    if (['i', 'I', 'v', 'V'].includes(value)) {
      this.answer.roman.numeral += value;
    }
    // Toggle the diminished on or off...
    if (['o', 'o/'].includes(value)) {
      this.answer.roman.dimCircle =
        this.answer.roman.dimCircle === value ? '' : (value as ChordIdAnswer['roman']['dimCircle']);
    }
    this.updateAnswerDisplay();
    return this.letterPanel.customButtonEvent(this.answerText);
  }

  private buttonCallback(type: ChordIdButtonType, value: string): void {
    if (value === 'check') {
      this.letterPanel.customButtonEvent(value);
      return;
    }

    switch (type) {
      case 'inversionNumbers':
        console.log('inversionNumbers');
        this.updateInversion(value as '0' | '1' | '2' | '3');
        break;
      case 'figures':
        console.log('figures');
        this.addFigureCharacterIfPossible(value);
        break;
      case 'quality':
        console.log('quality');
        this.updateQuality(value as any);
        break;
      case 'sopranoFactor':
        console.log('sopranoFactor');
        this.updateSopranoFactor(value as SopranoFactorString);
        break;
      case 'uiAction':
        console.log('uiAction');
        if (value === 'undo') this.undo();
        break;
    }
    this.removeUnnecessaryAnswerStashEntryIfNeeded();
    this.updateAnswerDisplay();
    this.letterPanel.customButtonEvent(this.answerText);
  }

  private updateInversion(inversion: '0' | '1' | '2' | '3') {
    this.stashAnswer();
    this.answer.inversion = inversion;
  }

  private addFigureCharacterIfPossible(value: string): void {
    // be sure its numeric
    if (!isNumeric(value)) return;

    // Only allow up to 3 figures:
    if (this.answer.inversion.length > 3) return;

    this.stashAnswer();
    // Add the new figure to the existing ones:
    this.answer.inversion = `${this.answer.inversion || ''}${value}`;

    if (Chord.isValidFigures(this.answer.inversion)) {
      this.setIsSeventh(Chord.figuresAreSeventh(this.answer.inversion));
    }
  }

  private stashAnswer() {
    this.answerStack.push(utils.clone(this.answer, true));
  }

  private popAnswer() {
    this.answer = this.answerStack.pop() || this.answer;
  }

  private removeUnnecessaryAnswerStashEntryIfNeeded() {
    if (!this.answerStack.length) return;
    const lastAnswer = this.answerStack[this.answerStack.length - 1]!;
    if (isEqual(lastAnswer, this.answer)) {
      this.answerStack = this.answerStack.slice(0, -1);
    }
  }

  private setIsSeventh(isSeventh: boolean) {
    this.answer.isSeventh = isSeventh;
  }

  public updateRoot(note: Note): void {
    if (!this.answer.root || !note.equals(this.answer.root)) {
      this.stashAnswer();
      this.answer.root = note;
      this.updateAnswerDisplay();
      this.letterPanel.customButtonEvent(this.answerText);
    }
  }

  private updateSopranoFactor(value: SopranoFactorString): void {
    if (!this.answer.sopranoFactor || this.answer.sopranoFactor !== value) {
      this.stashAnswer();
      this.answer.sopranoFactor = value;
    }
  }

  private updateQuality(
    value:
      | 'Maj'
      | 'Maj7'
      | 'min'
      | 'min7'
      | 'Aug'
      | 'dim'
      | 'dim7'
      | 'Dom'
      | 'Dom7'
      | 'half-dim'
      | 'half-dim7'
      | '7'
  ): void {
    console.log('updateQuality', value);
    this.stashAnswer();
    // This happens when triad & seventh quality buttons are available, but
    // figure buttons are not visible. Instead we show a single '7' button to
    // allow making Maj 7 possible.
    if (value === '7') {
      this.setIsSeventh(!this.answer.isSeventh);
      // If a quality hasn't been set, default it to dominant:
      if (!this.answer.quality) this.answer.quality = 'Dom';
      return;
    }

    // The value may or may not include 7. If it does, set isSeventh true, and set
    // the quality to the value WITHOUT the 7.
    if (value.includes('7')) {
      this.setIsSeventh(true);
      this.answer.quality = value.replace('7', '');
    } else {
      if (this.options.inversionType !== 'figures') {
        this.answer.isSeventh = false;
      }
      this.answer.quality = value;
    }
    console.log('this.answer.quality', this.answer.quality);
  }

  undo(): void {
    this.popAnswer();
  }

  get answerText(): string {
    return this._answerText || this.updateAnswerDisplay();
  }

  updateAnswerDisplay(): string {
    const fontRN = this.answer.roman.numeral
      .split('')
      .filter((c): c is 'i' | 'I' | 'v' | 'V' => ['i', 'I', 'v', 'V'].includes(c))
      .map((c) => Font.toHTML('romanNumeral', c))
      .join('');
    const dimCircle = Font.toHTML('quality', this.answer.roman.dimCircle) || '';
    const rn = `${fontRN}${dimCircle}`;

    let quality = this.answer.quality || '';
    if (this.options.inversionType !== 'figures') {
      if (this.answer.isSeventh) quality += FIGURE('7');
    }

    let sopranoFactor = '';
    if (this.answer.sopranoFactor && this.options.sopranoFactor) {
      sopranoFactor = `Sop: ${this.answer.sopranoFactor}`;
    }

    let inversion = '';
    if (this.answer.inversion !== '') {
      if (this.options.inversionType === 'numbers') {
        inversion = ["Root pos'n", '1st inv.', '2nd inv.', '3rd inv.'][
          Number(this.answer.inversion)
        ] as string;
      } else if (this.options.inversionType === 'figures') {
        inversion = FIGURE(`${this.answer.inversion}`);
      }
    }

    const root =
      this.answer.root && this.answer.root instanceof Note
        ? this.answer.root.toAccidentalFontShortString()
        : '';

    const answerDisplay = [];
    if (this.options.romans && (this.answer.roman.numeral || this.answer.roman.dimCircle)) {
      answerDisplay.push(rn);
    }
    if (this.options.root && this.answer.root) answerDisplay.push(root);
    if (quality) answerDisplay.push(quality);
    if (this.options.inversionType === 'numbers') answerDisplay.push(inversion);

    let answerString = answerDisplay.join(' ');
    if (this.options.inversionType === 'figures') {
      answerString += inversion;
    }
    if (sopranoFactor) {
      answerString += answerString.length ? ', ' : '';
      answerString += sopranoFactor;
    }

    this._answerText = answerString || '_'.repeat(this.options.numUnderscoresForBlankQuestion || 2);
    return this._answerText;
  }
}
